import type { CSSProperties } from "react";

import { Mascot, type MascotName } from "../components/Mascot";
import { TreatIcon } from "../components/TreatIcon";

type Plan = {
  key: string;
  name: string;
  tagline: string;
  price: string;
  cadence: string;
  treatLabel: string;
  description: string;
  cta: string;
  accent: string;
  tint: string;
  softBg: string;
  mascot: MascotName;
  features: string[];
};

const PLANS: Plan[] = [
  {
    key: "treat-pack",
    name: "Treat Pack",
    tagline: "Top up anytime",
    price: "$10",
    cadence: "one-time",
    treatLabel: "500 Treats",
    description:
      "For anyone needing a top-up when their monthly Treats run out.",
    cta: "Get started",
    accent: "#F59E0B",
    tint: "#FEF3C7",
    softBg: "linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 60%)",
    mascot: "Quill",
    features: [
      "500 Treats — never expire",
      "Works with any Scribenote plan",
      "All Companions included",
    ],
  },
  {
    key: "munchkin",
    name: "Munchkin",
    tagline: "For daily workflow",
    price: "$35",
    cadence: "/month",
    treatLabel: "2,000 Treats / month",
    description:
      "Perfect for vets using Companion as part of their daily workflow.",
    cta: "Get started",
    accent: "#6366F1",
    tint: "#E0E7FF",
    softBg: "linear-gradient(180deg, #EEF2FF 0%, #FFFFFF 60%)",
    mascot: "Otto",
    features: [
      "2,000 Treats refilled monthly",
      "Works with any Scribenote plan",
      "All Companions included",
    ],
  },
  {
    key: "chonk",
    name: "Chonk",
    tagline: "For power users",
    price: "$150",
    cadence: "/month",
    treatLabel: "10,000 Treats / month",
    description: "Ideal for power users and heavy Companion workflows.",
    cta: "Get started",
    accent: "#EC4899",
    tint: "#FCE7F3",
    softBg: "linear-gradient(180deg, #FDF2F8 0%, #FFFFFF 60%)",
    mascot: "Summi",
    features: [
      "10,000 Treats refilled monthly",
      "Works with any Scribenote plan",
      "All Companions included",
      "Best per-Treat value",
    ],
  },
];

type PlanCardStyle = CSSProperties & {
  "--accent": string;
  "--tint": string;
  "--soft-bg": string;
};

function PricingCard({ plan, ctaHref }: { plan: Plan; ctaHref: string }) {
  return (
    <div
      style={
        {
          "--accent": plan.accent,
          "--tint": plan.tint,
          "--soft-bg": plan.softBg,
        } as PlanCardStyle
      }
      className="pricing-card relative flex flex-col gap-5 overflow-hidden rounded-3xl border border-grindigo-100 bg-[image:var(--soft-bg)] p-8 shadow-[0_18px_38px_-22px_rgba(99,102,241,0.18)] transition-[transform,box-shadow] duration-[350ms] ease-[cubic-bezier(0.2,0.9,0.3,1)]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-20 -right-16 h-60 w-60 rounded-full bg-[radial-gradient(circle,var(--tint)_0%,transparent_70%)] opacity-90"
      />

      <div className="relative z-[1] flex items-center gap-3.5">
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full shadow-[0_6px_16px_-8px_var(--accent)]">
          <Mascot name={plan.mascot} size={56} />
        </div>
        <div>
          <div className="font-companion mb-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--accent)]">
            {plan.tagline}
          </div>
          <h3 className="font-companion m-0 text-[26px] font-bold leading-[1.05] tracking-[-0.02em] text-primary">
            {plan.name}
          </h3>
        </div>
      </div>

      <div className="relative z-[1] border-b border-dashed border-grindigo-200 pb-4">
        <div className="mb-2 flex items-baseline gap-1.5">
          <span className="font-companion text-[52px] font-bold leading-none tracking-[-0.03em] text-grindigo-950">
            {plan.price}
          </span>
          <span className="text-base font-medium text-secondary">
            {plan.cadence}
          </span>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border border-[var(--tint)] bg-white px-3 py-1 text-[13px] font-semibold text-grindigo-800">
          <TreatIcon size={14} color={plan.accent} />
          {plan.treatLabel}
        </div>
      </div>

      <p className="relative z-[1] m-0 text-[14.5px] leading-[1.55] text-secondary [text-wrap:pretty]">
        {plan.description}
      </p>

      <ul className="relative z-[1] m-0 flex list-none flex-col gap-2.5 p-0">
        {plan.features.map((f) => (
          <li
            key={f}
            className="flex items-start gap-2.5 text-sm leading-[1.45] text-primary"
          >
            <span className="mt-0.5 grid h-4.5 w-4.5 shrink-0 place-items-center rounded-full bg-[var(--tint)] text-[var(--accent)]">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            {f}
          </li>
        ))}
      </ul>

      <div className="relative z-[1] mt-auto pt-2">
        <a
          href={ctaHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border-[1.5px] border-[var(--accent)] bg-white px-5 py-3.5 font-body text-[15px] font-semibold text-[var(--accent)] no-underline transition-[transform,box-shadow,background] duration-150 ease-[ease] hover:-translate-y-px hover:bg-[var(--tint)]"
        >
          {plan.cta}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12h14M13 5l7 7-7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>
    </div>
  );
}

type PricingCardsProps = {
  ctaHref: string;
};

export function PricingCards({ ctaHref }: PricingCardsProps) {
  return (
    <section className="relative px-[clamp(24px,5vw,80px)] pb-[clamp(48px,6vw,80px)]">
      <div className="pricing-grid mx-auto grid max-w-[1240px] grid-cols-1 items-stretch gap-6 lg:grid-cols-3">
        {PLANS.map((plan) => (
          <PricingCard key={plan.key} plan={plan} ctaHref={ctaHref} />
        ))}
      </div>
    </section>
  );
}
